/**
 * What a map renderer tells the Overview's tap bubble, beside the four
 * selection callbacks (UI round of 2026-11-02): where a device is on screen
 * now, that a tap landed on nothing, and what the mouse is over.
 *
 * All optional and all read-only. A renderer still SELECTS through
 * `MapSelectionHandlers` and nothing else, so neither renderer gains a way
 * to command anything; a screen with no bubble passes none of this.
 */
export type MapDeviceKind = 'robot' | 'lift' | 'door';

export interface MapDevice {
  readonly kind: MapDeviceKind;
  /** As the selection callback names it: a robot by its bare name. */
  readonly id: string;
}

export type MapAnchor =
  /** Drawn here, in viewport coordinates. */
  | { readonly state: 'visible'; readonly x: number; readonly y: number }
  /** Drawn on another floor, which the person can be offered. */
  | { readonly state: 'other-floor'; readonly level: string }
  /** Not drawn at all: lost, riding out of sight, behind the camera. */
  | { readonly state: 'no-anchor' };

export const NO_ANCHOR: MapAnchor = { state: 'no-anchor' };

/** Read every frame by the bubble, so it must only look things up. */
export interface MapAnchorSource {
  anchorOf(device: MapDevice): MapAnchor;
}

export interface MapBubbleRendererProps {
  /** Filled by the renderer while it is mounted. */
  readonly anchorSourceRef?: { current: MapAnchorSource | null };
  /** A tap that hit no device, zone or button. */
  readonly onTapNothing?: () => void;
  /** Mouse only, and never during a press: what the pointer is over, or
   *  null, with where the pointer is in viewport coordinates. */
  readonly onHoverDevice?: (device: MapDevice | null, x: number, y: number) => void;
}
